// INT-945: tracks funnel UUIDs evicted by FunnelCacheInvalidationProvider so
// any concurrent `Chat.hook.resetFunnelInfo` fetch in flight at eviction time
// does not re-insert the just-evicted entry with stale data.
//
// The race the resurrection bug fix addresses:
//
//   T=0   resetFunnelInfo(F) — cache miss → starts ChatService.getWorkspacesInfo
//   T=1s  funnel.updated event arrives → provider evicts F from cache, marks F
//   T=2s  fetch resolves with the pre-eviction snapshot. Without this guard the
//         resolver would call setFunnelInfo([...prev, F]) and re-cache the
//         stale users[] — the dropdown stays wrong until the next reload.
//
// The 30-second TTL covers normal fetch durations (typically < 2s). Slow
// fetches beyond the TTL window remain a residual risk (mitigated by the
// global subscriber refiring on the next backend event).

export const RECENTLY_EVICTED_TTL_MS = 30_000;

const recentlyEvictedFunnels = new Set<string>();
const evictionTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

export function markFunnelRecentlyEvicted(uuid: string): void {
  recentlyEvictedFunnels.add(uuid);
  // Resetting the TTL on re-mark — clear any pending timeout so a fresh
  // 30s window starts from this call.
  const existing = evictionTimeouts.get(uuid);
  if (existing) clearTimeout(existing);
  const timeoutId = setTimeout(() => {
    recentlyEvictedFunnels.delete(uuid);
    evictionTimeouts.delete(uuid);
  }, RECENTLY_EVICTED_TTL_MS);
  evictionTimeouts.set(uuid, timeoutId);
}

export function wasFunnelRecentlyEvicted(uuid: string): boolean {
  return recentlyEvictedFunnels.has(uuid);
}

// Test-only seam: per-test reset for vitest isolation. The Set is
// module-scoped so vitest's `clearAllMocks()` does not touch it.
export function clearAllRecentlyEvictedFunnels(): void {
  for (const timeoutId of evictionTimeouts.values()) {
    clearTimeout(timeoutId);
  }
  evictionTimeouts.clear();
  recentlyEvictedFunnels.clear();
}
